<template>
    <div class="container-fluid">
        <!-- 헤더 섹션 -->
        <div class="row">
            <div class="col-lg-12">
                <div class="d-flex flex-wrap align-items-center justify-content-between">
                    <div>
                        <h4 class="mb-3">사용자 권한 관리 (옴니)</h4>
                        <p class="mb-0">모든 사용자의 모든 권한을 일괄 관리할 수 있습니다</p>
                    </div>
                    <div class="d-flex flex-wrap">
                        <b-button variant="success" class="mr-2 mb-2" @click="showBulkUpdateModal">
                            <i class="las la-users mr-1"></i> 일괄 권한 설정
                        </b-button>
                        <b-button variant="danger" class="mr-2 mb-2" @click="showBulkDeleteModal">
                            <i class="las la-trash mr-1"></i> 일괄 삭제
                        </b-button>
                        <b-button variant="info" class="mr-2 mb-2" @click="showCompanyChangeModal">
                            <i class="las la-building mr-1"></i> 회사ID 변경
                        </b-button>
                        <b-button variant="warning" class="mr-2 mb-2" @click="showPasswordResetModal">
                            <i class="las la-key mr-1"></i> 비밀번호 초기화
                        </b-button>
                        <b-button variant="secondary" class="mr-2 mb-2" @click="showBulkApprovalModal">
                            <i class="las la-check-circle mr-1"></i> 일괄 승인
                        </b-button>
                        <b-button variant="dark" class="mr-2 mb-2" @click="showAdvancedFeaturesModal">
                            <i class="las la-cogs mr-1"></i> 고급 기능
                        </b-button>
                        <b-button variant="primary" @click="refreshUserList">
                            <i class="las la-sync mr-1"></i> 새로고침
                        </b-button>
                    </div>
                </div>
            </div>
        </div>

        <!-- 검색 필터 -->
        <div class="row mt-4">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-4">
                                <b-form-group label="이메일 검색">
                                    <b-form-input
                                        v-model="searchEmail"
                                        placeholder="이메일을 입력하세요"
                                        @input="filterUsers"
                                    ></b-form-input>
                                </b-form-group>
                            </div>
                            <div class="col-md-4">
                                <b-form-group label="회사명 검색">
                                    <b-form-input
                                        v-model="searchCompany"
                                        placeholder="회사명을 입력하세요"
                                        @input="filterUsers"
                                    ></b-form-input>
                                </b-form-group>
                            </div>
                            <div class="col-md-4">
                                <b-form-group label="승인 상태">
                                    <b-form-select
                                        v-model="filterApproval"
                                        @change="filterUsers"
                                    >
                                        <option value="">전체</option>
                                        <option value="true">승인됨</option>
                                        <option value="false">미승인</option>
                                    </b-form-select>
                                </b-form-group>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 사용자 권한 테이블 -->
        <div class="row mt-4">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-body">
                        <CDataTable
                            storage-key="UserAuthManager"
                            :headers="tableHeaders"
                            :items="filteredUsers"
                            :loading="loading"
                            item-key="email"
                            height="70vh"
                            :show-select="false"
                            :is-per-page="true"
                            :is-column="true"
                            :is-radio="true"
                        >
                            <!-- 사용자 정보 슬롯 -->
                            <template v-slot:userInfo="{ item }">
                                <div>
                                    <strong>{{ item.email }}</strong>
                                    <br>
                                    <small class="text-muted">{{ item.name }}</small>
                                </div>
                            </template>

                            <!-- 회사 정보 슬롯 -->
                            <template v-slot:companyInfo="{ item }">
                                <div>
                                    {{ item.companyid }}
                                    <br>
                                    <small class="text-muted">{{ item.phone }}</small>
                                </div>
                            </template>

                            <!-- 승인 상태 슬롯 -->
                            <template v-slot:approval="{ item }">
                                <b-badge
                                    :variant="item.approval ? 'success' : 'warning'"
                                    @click="toggleSingleAuth(item, 'approval')"
                                    class="auth-badge"
                                >
                                    {{ item.approval ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <!-- 권한 슬롯들 -->
                            <template v-slot:showNomadMenu="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.showNomadMenu)"
                                    @click="toggleSingleAuth(item, 'showNomadMenu')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.showNomadMenu ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:showgrowth="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.showgrowth)"
                                    @click="toggleSingleAuth(item, 'showgrowth')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.showgrowth ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:showSCM="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.showSCM)"
                                    @click="toggleSingleAuth(item, 'showSCM')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.showSCM ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:showAiRocketReg="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.showAiRocketReg)"
                                    @click="toggleSingleAuth(item, 'showAiRocketReg')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.showAiRocketReg ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:showCollectRank="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.showCollectRank)"
                                    @click="toggleSingleAuth(item, 'showCollectRank')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.showCollectRank ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:showKeywordCollect="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.showKeywordCollect)"
                                    @click="toggleSingleAuth(item, 'showKeywordCollect')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.showKeywordCollect ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:showKeywordAnalysis="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.showKeywordAnalysis)"
                                    @click="toggleSingleAuth(item, 'showKeywordAnalysis')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.showKeywordAnalysis ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:makeProductName="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.makeProductName)"
                                    @click="toggleSingleAuth(item, 'makeProductName')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.makeProductName ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:showNomadKeyword="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.showNomadKeyword)"
                                    @click="toggleSingleAuth(item, 'showNomadKeyword')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.showNomadKeyword ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:showNomadCategory="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.showNomadCategory)"
                                    @click="toggleSingleAuth(item, 'showNomadCategory')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.showNomadCategory ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:shownomadv3="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.shownomadv3)"
                                    @click="toggleSingleAuth(item, 'shownomadv3')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.shownomadv3 ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:showNomadAdManage="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.showNomadAdManage)"
                                    @click="toggleSingleAuth(item, 'showNomadAdManage')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.showNomadAdManage ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:showAdManager="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.showAdManager)"
                                    @click="toggleSingleAuth(item, 'showAdManager')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.showAdManager ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:showSellerInfo="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.showSellerInfo)"
                                    @click="toggleSingleAuth(item, 'showSellerInfo')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.showSellerInfo ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <template v-slot:showdeveloper="{ item }">
                                <b-badge
                                    :variant="getAuthVariant(item.auth.showdeveloper)"
                                    @click="toggleSingleAuth(item, 'showdeveloper')"
                                    class="auth-badge"
                                >
                                    {{ item.auth.showdeveloper ? 'ON' : 'OFF' }}
                                </b-badge>
                            </template>

                            <!-- 메모 슬롯 -->
                            <template v-slot:memo="{ item }">
                                <b-form-textarea
                                    v-model="item.memo"
                                    rows="2"
                                    max-rows="4"
                                    @blur="updateSingleMemo(item)"
                                    placeholder="메모"
                                    style="font-size: 12px; min-width: 150px;"
                                ></b-form-textarea>
                            </template>

                            <!-- 작업 슬롯 -->
                            <template v-slot:actions="{ item }">
                                <b-button
                                    size="sm"
                                    variant="danger"
                                    @click="deleteSingleUser(item)"
                                >
                                    삭제
                                </b-button>
                            </template>
                        </CDataTable>
                    </div>
                </div>
            </div>
        </div>

        <!-- 일괄 권한 설정 모달 -->
        <b-modal
            id="bulkUpdateModal"
            title="일괄 권한 설정"
            size="lg"
            @ok="processBulkUpdate"
        >
            <div class="row">
                <div class="col-md-6">
                    <b-form-group label="대상 이메일 (한 줄에 하나씩 입력)">
                        <b-form-textarea
                            v-model="bulkEmails"
                            rows="10"
                            placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                        ></b-form-textarea>
                        <small class="text-muted">여러 이메일을 붙여넣기 하세요</small>
                    </b-form-group>
                </div>
                <div class="col-md-6">
                    <b-form-group label="권한 설정">
                        <div class="row">
                            <div class="col-6" v-for="(label, key) in authOptions" :key="key">
                                <b-form-checkbox
                                    v-model="bulkAuthSettings[key]"
                                    :indeterminate="bulkAuthSettings[key] === null"
                                    @change="handleAuthChange(key, $event)"
                                >
                                    {{ label }}
                                </b-form-checkbox>
                            </div>
                        </div>
                        <hr>
                        <b-form-group label="승인 상태">
                            <b-form-checkbox
                                v-model="bulkApproval"
                                :indeterminate="bulkApproval === null"
                                @change="handleApprovalChange"
                            >
                                승인
                            </b-form-checkbox>
                        </b-form-group>
                        <b-form-group label="메모">
                            <b-form-input v-model="bulkMemo" placeholder="일괄 적용할 메모"></b-form-input>
                        </b-form-group>
                    </b-form-group>
                </div>
            </div>
        </b-modal>

        <!-- 일괄 삭제 모달 -->
        <b-modal
            id="bulkDeleteModal"
            title="일괄 사용자 삭제"
            @ok="processBulkDelete"
            ok-variant="danger"
        >
            <b-form-group label="삭제할 이메일 (한 줄에 하나씩 입력)">
                <b-form-textarea
                    v-model="deleteEmails"
                    rows="10"
                    placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                ></b-form-textarea>
                <small class="text-danger">⚠️ 이 작업은 되돌릴 수 없습니다!</small>
            </b-form-group>
        </b-modal>

        <!-- 회사ID 변경 모달 -->
        <b-modal
            id="companyChangeModal"
            title="회사ID 변경"
            @ok="processCompanyChange"
            size="lg"
        >
            <div class="row">
                <div class="col-md-6">
                    <b-form-group label="대상 이메일 (한 줄에 하나씩 입력)">
                        <b-form-textarea
                            v-model="companyChangeEmails"
                            rows="8"
                            placeholder="user1@example.com&#10;user2@example.com"
                        ></b-form-textarea>
                    </b-form-group>
                </div>
                <div class="col-md-6">
                    <b-form-group label="새 회사ID">
                        <b-form-input v-model="newCompanyId" placeholder="새로운 회사ID 입력"></b-form-input>
                    </b-form-group>
                    <b-form-group label="새 공급업체 코드 (선택사항)">
                        <b-form-input v-model="newSupplierCode" placeholder="새로운 공급업체 코드"></b-form-input>
                    </b-form-group>
                </div>
            </div>
        </b-modal>

        <!-- 비밀번호 초기화 모달 -->
        <b-modal
            id="passwordResetModal"
            title="비밀번호 초기화"
            @ok="processPasswordReset"
        >
            <b-form-group label="대상 이메일 (한 줄에 하나씩 입력)">
                <b-form-textarea
                    v-model="passwordResetEmails"
                    rows="8"
                    placeholder="user1@example.com&#10;user2@example.com"
                ></b-form-textarea>
            </b-form-group>
            <b-form-group label="새 비밀번호">
                <b-form-input v-model="newPassword" type="password" placeholder="새로운 비밀번호 (영문·숫자·특수문자 8~20자)"></b-form-input>
            </b-form-group>
        </b-modal>

        <!-- 일괄 승인 모달 -->
        <b-modal
            id="bulkApprovalModal"
            title="일괄 승인 처리"
            @ok="processBulkApproval"
        >
            <div class="row">
                <div class="col-md-6">
                    <b-form-group label="대상 이메일 (한 줄에 하나씩 입력)">
                        <b-form-textarea
                            v-model="approvalEmails"
                            rows="8"
                            placeholder="user1@example.com&#10;user2@example.com"
                        ></b-form-textarea>
                    </b-form-group>
                </div>
                <div class="col-md-6">
                    <b-form-group label="승인 상태">
                        <b-form-radio-group v-model="approvalStatus">
                            <b-form-radio value="approve">승인</b-form-radio>
                            <b-form-radio value="reject">승인 취소</b-form-radio>
                        </b-form-radio-group>
                    </b-form-group>
                    <b-form-group label="메모">
                        <b-form-textarea v-model="approvalMemo" rows="3" placeholder="승인 관련 메모"></b-form-textarea>
                    </b-form-group>
                </div>
            </div>
        </b-modal>

        <!-- 고급 기능 모달 -->
        <b-modal
            id="advancedFeaturesModal"
            title="고급 관리 기능"
            size="xl"
            hide-footer
        >
            <div class="row">
                <div class="col-md-4">
                    <h5>계정 관리</h5>
                    <b-button variant="outline-info" block class="mb-2" @click="showAccountCreationModal">
                        <i class="las la-user-plus mr-1"></i> 계정 생성
                    </b-button>
                    <b-button variant="outline-warning" block class="mb-2" @click="exportUserData">
                        <i class="las la-download mr-1"></i> 사용자 데이터 내보내기
                    </b-button>
                    <b-button variant="outline-secondary" block class="mb-2" @click="showUserStatsModal">
                        <i class="las la-chart-bar mr-1"></i> 사용자 통계
                    </b-button>
                </div>
                <div class="col-md-4">
                    <h5>권한 관리</h5>
                    <b-button variant="outline-success" block class="mb-2" @click="activateAllFeatures">
                        <i class="las la-check-double mr-1"></i> 모든 기능 활성화
                    </b-button>
                    <b-button variant="outline-danger" block class="mb-2" @click="deactivateAllFeatures">
                        <i class="las la-times-circle mr-1"></i> 모든 기능 비활성화
                    </b-button>
                    <b-button variant="outline-primary" block class="mb-2" @click="copyPermissions">
                        <i class="las la-copy mr-1"></i> 권한 복사
                    </b-button>
                </div>
                <div class="col-md-4">
                    <h5>시스템 관리</h5>
                    <b-button variant="outline-dark" block class="mb-2" @click="cleanupInactiveUsers">
                        <i class="las la-broom mr-1"></i> 비활성 사용자 정리
                    </b-button>
                    <b-button variant="outline-info" block class="mb-2" @click="sendNotification">
                        <i class="las la-bell mr-1"></i> 알림 발송
                    </b-button>
                    <b-button variant="outline-warning" block class="mb-2" @click="generateReport">
                        <i class="las la-file-alt mr-1"></i> 보고서 생성
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- 계정 생성 모달 -->
        <b-modal
            id="accountCreationModal"
            title="새 계정 생성"
            @ok="processAccountCreation"
            size="lg"
        >
            <div class="row">
                <div class="col-md-6">
                    <b-form-group label="이메일 목록 (한 줄에 하나씩)">
                        <b-form-textarea
                            v-model="newAccountEmails"
                            rows="8"
                            placeholder="user1@example.com&#10;user2@example.com"
                        ></b-form-textarea>
                    </b-form-group>
                </div>
                <div class="col-md-6">
                    <b-form-group label="기본 회사ID">
                        <b-form-input v-model="defaultCompanyId" placeholder="회사ID"></b-form-input>
                    </b-form-group>
                    <b-form-group label="기본 비밀번호">
                        <b-form-input v-model="defaultPassword" type="password" placeholder="기본 비밀번호 (영문·숫자·특수문자 8~20자)"></b-form-input>
                    </b-form-group>
                    <b-form-group label="기본 권한 템플릿">
                        <b-form-select v-model="permissionTemplate">
                            <option value="basic">기본 사용자</option>
                            <option value="advanced">고급 사용자</option>
                            <option value="admin">관리자</option>
                            <option value="custom">사용자 정의</option>
                        </b-form-select>
                    </b-form-group>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import CDataTable from '@/components/common/CDataTable';
import axios from 'axios';

export default {
    name: 'UserAuthManager',
    components: {
        CDataTable
    },
    data() {
        return {
            users: [],
            filteredUsers: [],
            loading: false,
            searchEmail: '',
            searchCompany: '',
            filterApproval: '',
            currentPage: 1,
            itemsPerPage: 20,

            // 일괄 업데이트용
            bulkEmails: '',
            bulkAuthSettings: {},
            bulkApproval: null,
            bulkMemo: '',

            // 일괄 삭제용
            deleteEmails: '',

            // 회사ID 변경용
            companyChangeEmails: '',
            newCompanyId: '',
            newSupplierCode: '',

            // 비밀번호 초기화용
            passwordResetEmails: '',
            newPassword: '',

            // 일괄 승인용
            approvalEmails: '',
            approvalStatus: 'approve',
            approvalMemo: '',

            // 계정 생성용
            newAccountEmails: '',
            defaultCompanyId: '',
            defaultPassword: '',
            permissionTemplate: 'basic',

            // 권한 옵션들
            authOptions: {
                showNomadMenu: '노마드메뉴',
                showgrowth: '그로스',
                showSCM: 'SCM',
                showAiRocketReg: 'AI로켓등록',
                showCollectRank: '수집랭킹',
                showKeywordCollect: '키워드수집',
                showKeywordAnalysis: '키워드분석',
                makeProductName: '상품명생성',
                showNomadKeyword: '노마드키워드',
                showNomadCategory: '노마드카테고리',
                shownomadv3: 'TF2',
                showNomadAdManage: '광고관리',
                showAdManager: '광고관리(대시보드)',
                showSellerInfo: '판매자정보',
                showdeveloper: '개발자',
                collectNomad: '노마드수집'
            },

            // 테이블 헤더 정의
            tableHeaders: [
                { text: '사용자 정보', value: 'userInfo', isSlot: true, width: '200px' },
                { text: '회사 정보', value: 'companyInfo', isSlot: true, width: '150px' },
                { text: '승인', value: 'approval', isSlot: true, width: '80px' },
                { text: '노마드메뉴', value: 'showNomadMenu', isSlot: true, width: '100px' },
                { text: '그로스', value: 'showgrowth', isSlot: true, width: '80px' },
                { text: 'SCM', value: 'showSCM', isSlot: true, width: '80px' },
                { text: 'AI로켓등록', value: 'showAiRocketReg', isSlot: true, width: '100px' },
                { text: '수집랭킹', value: 'showCollectRank', isSlot: true, width: '90px' },
                { text: '키워드수집', value: 'showKeywordCollect', isSlot: true, width: '100px' },
                { text: '키워드분석', value: 'showKeywordAnalysis', isSlot: true, width: '100px' },
                { text: '상품명생성', value: 'makeProductName', isSlot: true, width: '100px' },
                { text: '노마드키워드', value: 'showNomadKeyword', isSlot: true, width: '110px' },
                { text: '노마드카테고리', value: 'showNomadCategory', isSlot: true, width: '120px' },
                { text: 'TF2', value: 'shownomadv3', isSlot: true, width: '80px' },
                { text: '광고관리', value: 'showNomadAdManage', isSlot: true, width: '90px' },
                { text: '광고관리(대시보드)', value: 'showAdManager', isSlot: true, width: '130px' },
                { text: '판매자정보', value: 'showSellerInfo', isSlot: true, width: '100px' },
                { text: '개발자', value: 'showdeveloper', isSlot: true, width: '80px' },
                { text: '메모', value: 'memo', isSlot: true, width: '200px' },
                { text: '작업', value: 'actions', isSlot: true, width: '100px' }
            ]
        };
    },
    mounted() {
        // 개발자 권한 체크
        if (!this.$store.state.user.auth.showdeveloper) {
            this.$bvToast.toast('관리자 권한이 없습니다.', {
                title: '권한 없음',
                variant: 'warning',
                solid: true,
                toaster: 'b-toaster-bottom-right'
            });
            this.$router.push('/dashboard');
            return;
        }

        // CS 보조 패널이 ?email= 로 보내온 계정을 검색창에 미리 채운다.
        // 담당자가 이메일을 손으로 옮겨 치는 단계를 없애는 것이 목적이다.
        // 실측(2026-09-21): 「확인 후 회신」으로 미뤄진 건의 상당수가 계정변경·권한부여·부계정이고
        // 그 셋이 전부 이 화면 하나로 모인다.
        const qEmail = this.$route.query.email;
        if (typeof qEmail === 'string' && qEmail.trim()) {
            this.searchEmail = qEmail.trim();
        }

        this.initializeBulkSettings();
        this.loadUsers();
    },
    computed: {
        // CDataTable이 자체적으로 페이지네이션을 처리하므로 paginatedUsers는 제거
    },
    methods: {
        initializeBulkSettings() {
            // 일괄 설정 초기화
            Object.keys(this.authOptions).forEach(key => {
                this.$set(this.bulkAuthSettings, key, null);
            });
        },

        async loadUsers() {
            this.loading = true;
            try {
                const response = await axios.post('/api/admin/getAllUsers', {});
                if (response.data.res === 'success') {
                    this.users = response.data.result || [];
                    this.filteredUsers = [...this.users];
                    // 검색어가 이미 있으면(=?email= 로 들어온 경우) 바로 걸어준다.
                    // 여기서 안 부르면 filteredUsers 를 통째로 덮어써서 검색어가 무시된다.
                    if (this.searchEmail || this.searchCompany) {
                        this.filterUsers();
                    }
                } else {
                    throw new Error(response.data.message || '사용자 목록 로드 실패');
                }
            } catch (error) {
                console.error('사용자 목록 로드 실패:', error);
                this.$bvToast.toast('사용자 목록을 불러오는데 실패했습니다.', {
                    title: '오류',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
            } finally {
                this.loading = false;
            }
        },

        refreshUserList() {
            this.loadUsers();
        },

        filterUsers() {
            // ⚠ 값이 없는 계정이 섞여 있다. 옛날에는 `user.email.toLowerCase()` 였는데,
            //   **이메일이 없는 계정 한 건**에서 TypeError 가 나면서 Array.filter 가 통째로 죽었다.
            //   예외가 이벤트 핸들러 안에서 나서 화면에는 아무 표시도 없고, 표만 안 걸러졌다.
            //   14,233명 중 딱 1건 때문에 이 화면의 검색이 통째로 안 되고 있었다 (2026-09-22 실측).
            //   ⇒ 값이 없을 수 있는 필드는 전부 감싼다. 한 줄이 이상해도 나머지는 걸러져야 한다.
            this.filteredUsers = this.users.filter(user => {
                const email = (user.email || '').toLowerCase();
                const company = (user.companyid || '').toLowerCase();
                const emailMatch = !this.searchEmail || email.includes(this.searchEmail.toLowerCase());
                const companyMatch = !this.searchCompany || company.includes(this.searchCompany.toLowerCase());
                const approvalMatch = this.filterApproval === '' ||
                    String(user.approval) === this.filterApproval;

                return emailMatch && companyMatch && approvalMatch;
            });
            this.currentPage = 1; // 필터링 후 첫 페이지로
        },

        getAuthVariant(authValue) {
            return authValue ? 'success' : 'secondary';
        },

        // 개별 권한 토글
        async toggleSingleAuth(user, authName) {
            try {
                const updates = {};

                if (authName === 'approval') {
                    updates.approval = !user.approval;
                } else {
                    updates.auth = {};
                    updates.auth[authName] = !user.auth[authName];
                }

                const response = await axios.post('/api/admin/bulkUpdateUsers', {
                    emails: [user.email],
                    updates: updates
                });

                if (response.data.res === 'success') {
                    if (authName === 'approval') {
                        user.approval = !user.approval;
                    } else {
                        user.auth[authName] = !user.auth[authName];
                    }

                    this.$bvToast.toast('권한이 변경되었습니다.', {
                        title: '성공',
                        variant: 'success',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });
                } else {
                    throw new Error(response.data.message || '권한 변경 실패');
                }
            } catch (error) {
                console.error('권한 변경 실패:', error);
                this.$bvToast.toast('권한 변경에 실패했습니다.', {
                    title: '오류',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
            }
        },

        // 개별 메모 업데이트
        async updateSingleMemo(user) {
            try {
                const response = await axios.post('/api/admin/bulkUpdateUsers', {
                    emails: [user.email],
                    updates: { memo: user.memo }
                });

                if (response.data.res === 'success') {
                    this.$bvToast.toast('메모가 저장되었습니다.', {
                        title: '성공',
                        variant: 'success',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });
                }
            } catch (error) {
                console.error('메모 저장 실패:', error);
                this.$bvToast.toast('메모 저장에 실패했습니다.', {
                    title: '오류',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
            }
        },

        // 개별 사용자 삭제
        async deleteSingleUser(user) {
            if (!confirm(`${user.email} 사용자를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) {
                return;
            }

            try {
                const response = await axios.post('/api/admin/deleteUsers', {
                    emails: [user.email]
                });

                if (response.data.res === 'success') {
                    this.users = this.users.filter(u => u.email !== user.email);
                    this.filterUsers();

                    this.$bvToast.toast('사용자가 삭제되었습니다.', {
                        title: '성공',
                        variant: 'success',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });
                } else {
                    throw new Error(response.data.message || '사용자 삭제 실패');
                }
            } catch (error) {
                console.error('사용자 삭제 실패:', error);
                this.$bvToast.toast('사용자 삭제에 실패했습니다.', {
                    title: '오류',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
            }
        },

        // 모달 표시
        showBulkUpdateModal() {
            this.bulkEmails = '';
            this.bulkMemo = '';
            this.bulkApproval = null;
            this.initializeBulkSettings();
            this.$bvModal.show('bulkUpdateModal');
        },

        showBulkDeleteModal() {
            this.deleteEmails = '';
            this.$bvModal.show('bulkDeleteModal');
        },

        showCompanyChangeModal() {
            this.companyChangeEmails = '';
            this.newCompanyId = '';
            this.newSupplierCode = '';
            this.$bvModal.show('companyChangeModal');
        },

        showPasswordResetModal() {
            this.passwordResetEmails = '';
            this.newPassword = '';
            this.$bvModal.show('passwordResetModal');
        },

        showBulkApprovalModal() {
            this.approvalEmails = '';
            this.approvalStatus = 'approve';
            this.approvalMemo = '';
            this.$bvModal.show('bulkApprovalModal');
        },

        showAdvancedFeaturesModal() {
            this.$bvModal.show('advancedFeaturesModal');
        },

        showAccountCreationModal() {
            this.newAccountEmails = '';
            this.defaultCompanyId = '';
            this.defaultPassword = '';
            this.permissionTemplate = 'basic';
            this.$bvModal.hide('advancedFeaturesModal');
            this.$bvModal.show('accountCreationModal');
        },

        // 권한 체크박스 변경 핸들러
        handleAuthChange(key, value) {
            if (value === false) {
                this.bulkAuthSettings[key] = null; // 체크 해제시 null로 설정 (변경하지 않음)
            }
        },

        handleApprovalChange(value) {
            if (value === false) {
                this.bulkApproval = null;
            }
        },

        // 일괄 권한 업데이트 처리
        async processBulkUpdate() {
            if (!this.bulkEmails.trim()) {
                this.$bvToast.toast('이메일을 입력해주세요.', {
                    title: '입력 오류',
                    variant: 'warning',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
                return;
            }

            const emails = this.bulkEmails.split('\n')
                .map(email => email.trim())
                .filter(email => email.length > 0);

            if (emails.length === 0) {
                this.$bvToast.toast('유효한 이메일을 입력해주세요.', {
                    title: '입력 오류',
                    variant: 'warning',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
                return;
            }

            try {
                const updates = {};

                // 권한 설정
                const authUpdates = {};
                Object.keys(this.bulkAuthSettings).forEach(key => {
                    if (this.bulkAuthSettings[key] === true) {
                        authUpdates[key] = true;
                    } else if (this.bulkAuthSettings[key] === false) {
                        authUpdates[key] = false;
                    }
                });

                if (Object.keys(authUpdates).length > 0) {
                    updates.auth = authUpdates;
                }

                // 승인 상태
                if (this.bulkApproval !== null) {
                    updates.approval = this.bulkApproval;
                }

                // 메모
                if (this.bulkMemo.trim()) {
                    updates.memo = this.bulkMemo.trim();
                }

                if (Object.keys(updates).length === 0) {
                    this.$bvToast.toast('변경할 내용을 선택해주세요.', {
                        title: '입력 오류',
                        variant: 'warning',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });
                    return;
                }

                const response = await axios.post('/api/admin/bulkUpdateUsers', {
                    emails: emails,
                    updates: updates
                });

                if (response.data.res === 'success') {
                    const results = response.data.results;
                    const successCount = results.filter(r => r.success).length;
                    const failCount = results.length - successCount;

                    this.$bvToast.toast(`성공: ${successCount}명, 실패: ${failCount}명`, {
                        title: failCount > 0 ? '일괄 업데이트 — 실패 있음' : '일괄 업데이트 완료',
                        variant: failCount > 0 ? 'warning' : 'success',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });

                    this.loadUsers(); // 목록 새로고침
                } else {
                    throw new Error(response.data.message || '일괄 업데이트 실패');
                }
            } catch (error) {
                console.error('일괄 업데이트 실패:', error);
                this.$bvToast.toast('일괄 업데이트에 실패했습니다.', {
                    title: '오류',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
            }
        },

        // 일괄 삭제 처리
        async processBulkDelete() {
            if (!this.deleteEmails.trim()) {
                this.$bvToast.toast('삭제할 이메일을 입력해주세요.', {
                    title: '입력 오류',
                    variant: 'warning',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
                return;
            }

            const emails = this.deleteEmails.split('\n')
                .map(email => email.trim())
                .filter(email => email.length > 0);

            if (emails.length === 0) {
                this.$bvToast.toast('유효한 이메일을 입력해주세요.', {
                    title: '입력 오류',
                    variant: 'warning',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
                return;
            }

            try {
                const response = await axios.post('/api/admin/deleteUsers', {
                    emails: emails
                });

                if (response.data.res === 'success') {
                    const results = response.data.results;
                    const successCount = results.filter(r => r.success).length;
                    const failCount = results.length - successCount;

                    this.$bvToast.toast(`성공: ${successCount}명, 실패: ${failCount}명`, {
                        title: '일괄 삭제 완료',
                        variant: 'success',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });

                    this.loadUsers(); // 목록 새로고침
                } else {
                    throw new Error(response.data.message || '일괄 삭제 실패');
                }
            } catch (error) {
                console.error('일괄 삭제 실패:', error);
                this.$bvToast.toast('일괄 삭제에 실패했습니다.', {
                    title: '오류',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
            }
        },

        // 회사ID 변경 처리
        async processCompanyChange() {
            if (!this.companyChangeEmails.trim() || !this.newCompanyId.trim()) {
                this.$bvToast.toast('이메일과 새 회사ID를 입력해주세요.', {
                    title: '입력 오류',
                    variant: 'warning',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
                return;
            }

            const emails = this.companyChangeEmails.split('\n')
                .map(email => email.trim())
                .filter(email => email.length > 0);

            try {
                const updates = {
                    companyid: this.newCompanyId.trim()
                };

                if (this.newSupplierCode.trim()) {
                    updates.suppliercode = this.newSupplierCode.trim();
                }

                const response = await axios.post('/api/admin/bulkUpdateUsers', {
                    emails: emails,
                    updates: updates
                });

                if (response.data.res === 'success') {
                    const results = response.data.results;
                    const successCount = results.filter(r => r.success).length;

                    this.$bvToast.toast(`${successCount}명의 회사ID가 변경되었습니다.`, {
                        title: '회사ID 변경 완료',
                        variant: 'success',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });

                    this.loadUsers();
                } else {
                    throw new Error(response.data.message || '회사ID 변경 실패');
                }
            } catch (error) {
                console.error('회사ID 변경 실패:', error);
                this.$bvToast.toast('회사ID 변경에 실패했습니다.', {
                    title: '오류',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
            }
        },

        // 비밀번호 초기화 처리
        async processPasswordReset() {
            if (!this.passwordResetEmails.trim()) {
                this.$bvToast.toast('이메일을 입력해주세요.', {
                    title: '입력 오류',
                    variant: 'warning',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
                return;
            }

            const emails = this.passwordResetEmails.split('\n')
                .map(email => email.trim())
                .filter(email => email.length > 0);

            const password = this.newPassword.trim();
            if (!password) {
                this.$bvToast.toast('새 비밀번호를 넣어 주세요.', {
                    title: '입력 오류',
                    variant: 'warning',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
                return;
            }

            try {
                // 비밀번호 해시화는 서버에서 처리
                const response = await axios.post('/api/admin/bulkUpdateUsers', {
                    emails: emails,
                    updates: { password: password }
                });

                if (response.data.res === 'success') {
                    const results = response.data.results;
                    const successCount = results.filter(r => r.success).length;
                    const failCount = results.length - successCount;

                    this.$bvToast.toast(`성공: ${successCount}명, 실패: ${failCount}명`, {
                        title: failCount > 0 ? '비밀번호 초기화 — 실패 있음' : '비밀번호 초기화 완료',
                        variant: failCount > 0 ? 'warning' : 'success',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });
                } else {
                    this.$bvToast.toast(response.data.message || '비밀번호 초기화에 실패했습니다.', {
                        title: '비밀번호 초기화 안 됨',
                        variant: 'danger',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });
                }
            } catch (error) {
                console.error('비밀번호 초기화 실패:', error);
                this.$bvToast.toast('비밀번호 초기화에 실패했습니다.', {
                    title: '오류',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
            }
        },

        // 일괄 승인 처리
        async processBulkApproval() {
            if (!this.approvalEmails.trim()) {
                this.$bvToast.toast('이메일을 입력해주세요.', {
                    title: '입력 오류',
                    variant: 'warning',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
                return;
            }

            const emails = this.approvalEmails.split('\n')
                .map(email => email.trim())
                .filter(email => email.length > 0);

            try {
                const updates = {
                    approval: this.approvalStatus === 'approve'
                };

                if (this.approvalMemo.trim()) {
                    updates.memo = this.approvalMemo.trim();
                }

                const response = await axios.post('/api/admin/bulkUpdateUsers', {
                    emails: emails,
                    updates: updates
                });

                if (response.data.res === 'success') {
                    const results = response.data.results;
                    const successCount = results.filter(r => r.success).length;

                    this.$bvToast.toast(`${successCount}명의 승인 상태가 변경되었습니다.`, {
                        title: '일괄 승인 완료',
                        variant: 'success',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });

                    this.loadUsers();
                } else {
                    throw new Error(response.data.message || '일괄 승인 실패');
                }
            } catch (error) {
                console.error('일괄 승인 실패:', error);
                this.$bvToast.toast('일괄 승인에 실패했습니다.', {
                    title: '오류',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
            }
        },

        // 계정 생성 처리
        async processAccountCreation() {
            if (!this.newAccountEmails.trim()) {
                this.$bvToast.toast('이메일을 입력해주세요.', {
                    title: '입력 오류',
                    variant: 'warning',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
                return;
            }

            const emails = this.newAccountEmails.split('\n')
                .map(email => email.trim())
                .filter(email => email.length > 0);

            try {
                // 권한 템플릿에 따른 기본 권한 설정
                let defaultAuth = {};

                switch (this.permissionTemplate) {
                    case 'basic':
                        defaultAuth = {
                            showNomadMenu: true,
                            showgrowth: false,
                            showSCM: false
                        };
                        break;
                    case 'advanced':
                        defaultAuth = {
                            showNomadMenu: true,
                            showgrowth: true,
                            showSCM: true,
                            showAiRocketReg: true,
                            showCollectRank: true
                        };
                        break;
                    case 'admin':
                        Object.keys(this.authOptions).forEach(key => {
                            defaultAuth[key] = true;
                        });
                        break;
                }

                if (!this.defaultPassword.trim()) {
                    this.$bvToast.toast('기본 비밀번호를 넣어 주세요.', {
                        title: '입력 오류',
                        variant: 'warning',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });
                    return;
                }

                const response = await axios.post('/api/admin/createUsers', {
                    emails: emails,
                    defaultData: {
                        companyid: this.defaultCompanyId.trim() || 'DEFAULT',
                        password: this.defaultPassword.trim(),
                        auth: defaultAuth,
                        approval: false
                    }
                });

                if (response.data.res === 'success') {
                    const results = response.data.results;
                    const successCount = results.filter(r => r.success).length;
                    const failCount = results.length - successCount;

                    this.$bvToast.toast(`성공: ${successCount}개, 실패: ${failCount}개`, {
                        title: failCount > 0 ? '계정 생성 — 실패 있음' : '계정 생성 완료',
                        variant: failCount > 0 ? 'warning' : 'success',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });

                    this.loadUsers();
                } else {
                    this.$bvToast.toast(response.data.message || '계정 생성에 실패했습니다.', {
                        title: '계정 생성 안 됨',
                        variant: 'danger',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });
                }
            } catch (error) {
                console.error('계정 생성 실패:', error);
                this.$bvToast.toast('계정 생성에 실패했습니다.', {
                    title: '오류',
                    variant: 'danger',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
            }
        },

        // 고급 기능들
        async activateAllFeatures() {
            if (!confirm('선택된 사용자들의 모든 기능을 활성화하시겠습니까?')) return;

            // 구현 예정
            this.$bvToast.toast('기능이 준비 중입니다.', {
                title: '알림',
                variant: 'info',
                solid: true,
                toaster: 'b-toaster-bottom-right'
            });
        },

        async deactivateAllFeatures() {
            if (!confirm('선택된 사용자들의 모든 기능을 비활성화하시겠습니까?')) return;

            // 구현 예정
            this.$bvToast.toast('기능이 준비 중입니다.', {
                title: '알림',
                variant: 'info',
                solid: true,
                toaster: 'b-toaster-bottom-right'
            });
        },

        copyPermissions() {
            this.$bvToast.toast('권한 복사 기능이 준비 중입니다.', {
                title: '알림',
                variant: 'info',
                solid: true,
                toaster: 'b-toaster-bottom-right'
            });
        },

        exportUserData() {
            // CSV 내보내기
            const csvContent = this.generateCSV();
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `users_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        generateCSV() {
            const headers = ['이메일', '이름', '회사ID', '승인상태', '메모', ...Object.values(this.authOptions)];
            const rows = this.users.map(user => [
                user.email,
                user.name || '',
                user.companyid || '',
                user.approval ? '승인' : '미승인',
                user.memo || '',
                ...Object.keys(this.authOptions).map(key => user.auth[key] ? 'ON' : 'OFF')
            ]);

            return [headers, ...rows].map(row =>
                row.map(field => `"${field}"`).join(',')
            ).join('\n');
        },

        cleanupInactiveUsers() {
            this.$bvToast.toast('비활성 사용자 정리 기능이 준비 중입니다.', {
                title: '알림',
                variant: 'info',
                solid: true,
                toaster: 'b-toaster-bottom-right'
            });
        },

        sendNotification() {
            this.$bvToast.toast('알림 발송 기능이 준비 중입니다.', {
                title: '알림',
                variant: 'info',
                solid: true,
                toaster: 'b-toaster-bottom-right'
            });
        },

        generateReport() {
            this.$bvToast.toast('보고서 생성 기능이 준비 중입니다.', {
                title: '알림',
                variant: 'info',
                solid: true,
                toaster: 'b-toaster-bottom-right'
            });
        },

        showUserStatsModal() {
            this.$bvToast.toast('사용자 통계 기능이 준비 중입니다.', {
                title: '알림',
                variant: 'info',
                solid: true,
                toaster: 'b-toaster-bottom-right'
            });
        }
    }
};
</script>

<style scoped>
/* 권한 뱃지 스타일 */
.auth-badge {
    transition: all 0.2s ease;
    cursor: pointer;
    min-width: 35px;
    font-size: 11px;
    padding: 4px 8px;
}

.auth-badge:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

/* 카드 스타일 */
.card {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    border: 1px solid rgba(0, 0, 0, 0.125);
}

/* 버튼 스타일 */
.btn-sm {
    font-size: 11px;
    padding: 4px 8px;
}

/* 모달 스타일 */
.modal-lg {
    max-width: 900px;
}

.form-check-input:indeterminate {
    background-color: #6c757d;
    border-color: #6c757d;
}

/* CDataTable 내부 스타일 오버라이드 */
::v-deep .v-data-table {
    font-size: 13px;
}

::v-deep .v-data-table th {
    font-size: 12px;
    font-weight: 600;
}

::v-deep .v-data-table td {
    padding: 8px 12px !important;
    vertical-align: middle;
}

/* 텍스트 영역 스타일 */
::v-deep .form-control {
    font-size: 12px;
}

/* 작은 텍스트 스타일 */
.text-muted {
    font-size: 11px;
}

/* 반응형 조정 */
@media (max-width: 1200px) {
    .auth-badge {
        font-size: 10px;
        min-width: 30px;
        padding: 2px 6px;
    }

    ::v-deep .v-data-table {
        font-size: 12px;
    }
}
</style>
